import { Controller, useForm } from "react-hook-form";
import { BaseFormControlledModal } from "../../../../../../common/components/base_form_controlled_modal"
import { DefaultFormAutocomplete } from "../../../../../../common/components/default_form_autocomplete";
import { DefaultFormSelectInput } from "../../../../../../common/components/default_form_select_input";
import { DefaultFormDatePickerInput } from "../../../../../../common/components/default_form_date_picker_input";
import { ChangeEvent, useCallback, useEffect, useMemo } from "react";
import { zodResolver } from "@hookform/resolvers/zod";

import FormContainer from "../../../../../../common/components/form_container";
import DefaultFormInput from "../../../../../../common/components/default_form_input";
import dayjs from "dayjs";
import { InspecaoPatrimonioItensModel } from "../../../../domain/models/Inspecao_patrimonio_itens";
import { OcorrenciaInspecaoPatrimonioModel } from "../../../../domain/models/ocorrencia_inspecao_patrimonio";
import { OcorrenciaInspecaoPatrimonioForm, OcorrenciaInspecaoPatrimonioSchema } from "../../../schemas/PatrimonioItensInspecao_verificacao/ocorrencia_inspecao_patrimonio";
import { IconButton, styled } from "@mui/material";
import DynamicIcon from "../../../../../../common/components/dynamic_icon";
import PrimaryButton from "../../../../../../common/components/primary_button";
import { normalizeFileName } from "../../../../../../common/utils/utils";

/**
 * VisuallyHiddenInput
 */

const VisuallyHiddenInput = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1,
});

/**
 * OcorrenciaModal
 */

type Props = {
  open: boolean;
  saving: boolean;
  loading: boolean;
  inspecoesPatrimonioItens: InspecaoPatrimonioItensModel[];
  selectedInspecaoPatrimonioItem: InspecaoPatrimonioItensModel | null;
  selectedOcorrencia: OcorrenciaInspecaoPatrimonioModel | null;
  onDownload: (idOcorrencia: number, nroArquivo: number) => void,
  onDeleteFile: (nroArquivo: number) => void;
  onClose: () => void;
  onSubmit: (form: OcorrenciaInspecaoPatrimonioForm) => void;
};

export const OcorrenciaModal = ({
  open,
  saving,
  loading,
  inspecoesPatrimonioItens,
  selectedInspecaoPatrimonioItem,
  selectedOcorrencia,
  onDownload,
  onDeleteFile,
  onClose,
  onSubmit,
}: Props) => {
  const hookForm = useForm<OcorrenciaInspecaoPatrimonioForm>({
    mode: "onSubmit",
    reValidateMode: "onSubmit",
    resolver: zodResolver(OcorrenciaInspecaoPatrimonioSchema),
  });

  const inspecaoOptions = useMemo(() => {
    return [
      {
        id: 1,
        label: '1ª Inspeção'
      },
      {
        id: 2,
        label: '2ª Inspeção'
      },
    ];
  }, [
  ]);

  useEffect(() => {
    if (open) {
      if (selectedOcorrencia) {
        hookForm.setValue('idInspecaoPatrimonioItens', selectedOcorrencia.idInspecaoPatrimonioItens ?? null);
        hookForm.setValue('descricao', selectedOcorrencia.descricao ?? '');
        hookForm.setValue('dataCriacao', selectedOcorrencia.dataCriacao ? dayjs(selectedOcorrencia.dataCriacao).toDate() : null);
        hookForm.setValue('inspecao', selectedOcorrencia.inspecao ?? null);
        hookForm.setValue('nomeArquivo1', selectedOcorrencia.nomeArquivo1 ?? null);
        hookForm.setValue('nomeArquivo2', selectedOcorrencia.nomeArquivo2 ?? null);
        hookForm.setValue('descricaoArquivo1', selectedOcorrencia.descricaoArquivo1 ?? '');
        hookForm.setValue('descricaoArquivo2', selectedOcorrencia.descricaoArquivo2 ?? '');
      } else {
        hookForm.reset({
          idInspecaoPatrimonioItens: selectedInspecaoPatrimonioItem && selectedInspecaoPatrimonioItem.idInspecaoPatrimonioItens
            ? selectedInspecaoPatrimonioItem.idInspecaoPatrimonioItens
            : null,
          inspecao: 1,
          dataCriacao: new Date(),
        });
      }
    }
  }, [
    open,
    selectedOcorrencia,
    selectedInspecaoPatrimonioItem,
    hookForm,
  ]);

  const handleDownload = useCallback((nroArquivo: number) => {
    if (!saving && selectedOcorrencia)
      if (nroArquivo === 1)
        onDownload(selectedOcorrencia.idOcorrenciaInspecaoPatrimonio ?? 0, 1);
      else if (nroArquivo === 2)
        onDownload(selectedOcorrencia.idOcorrenciaInspecaoPatrimonio ?? 0, 2);
  }, [
    saving,
    selectedOcorrencia,
    onDownload,
  ]);

  const handleDeleteFile = useCallback((nroArquivo: number) => {
    if (!saving) {
      if (window.confirm('Você deseja excluir este item?')) {
        if (nroArquivo === 1) {
          hookForm.setValue('limparArquivo1', true);
          hookForm.setValue('arquivo1', null);
          hookForm.setValue('nomeArquivo1', null);
        } else if (nroArquivo === 2) {
          hookForm.setValue('limparArquivo2', true);
          hookForm.setValue('arquivo2', null);
          hookForm.setValue('nomeArquivo2', null);
        }
        onDeleteFile(nroArquivo);
      }
    }
  }, [
    saving,
    hookForm,
    onDeleteFile,
  ]);

  const handleFileChange = useCallback((e: ChangeEvent<HTMLInputElement>, onChange: (e: any) => void, nroArquivo: number) => {
    if (!saving && e.target.files && e.target.files.length > 0) {
      if (nroArquivo === 1) {
        hookForm.setValue('limparArquivo1', false);
        hookForm.setValue('arquivo1', null);
        hookForm.setValue('limparArquivo1', false);
      } else if (nroArquivo === 2) {
        hookForm.setValue('limparArquivo2', false);
        hookForm.setValue('arquivo2', null);
        hookForm.setValue('limparArquivo2', false);
      }
      onChange(e.target.files.item(0));
    }
  }, [
    saving,
    hookForm,
  ]);

  return (
    <BaseFormControlledModal
      open={open}
      saving={saving}
      loading={loading}
      title='Ocorrência'
      width="60rem"
      onClose={onClose}
      onSubmit={hookForm.handleSubmit(onSubmit)}>
      <FormContainer title="Informações" arrangement="fullRow" border={false} padding="small">
        <>
          <DefaultFormAutocomplete
            name='idInspecaoPatrimonioItens'
            label='Item de Inspeção'
            options={inspecoesPatrimonioItens}
            getOptionKey={x => x.idInspecaoPatrimonioItens}
            getOptionLabel={x => x.descricao ?? ''}
            reactHookForm={hookForm}
          />

          <DefaultFormSelectInput
            name='inspecao'
            label='Inspeção'
            options={inspecaoOptions}
            reactHookForm={hookForm}
          />
        </>
      </FormContainer>

      <FormContainer title="Descrição" arrangement="fullRow" border={false} padding="small">
        <>
          <DefaultFormDatePickerInput
            name='dataCriacao'
            label='Data'
            reactHookForm={hookForm}
          />

          <DefaultFormInput
            name='descricao'
            label='Descrição'
            required={true}
            multiline={true}
            rows={8}
            reactHookForm={hookForm}
          />
        </>
      </FormContainer>
      <FormContainer title={"Arquivo"} arrangement="fullRow" padding="small">
        <>
          <div className="flex flex-row gap-2 w-full justify-between">
            <div className="w-[30%]">
              <Controller
                name='arquivo1'
                control={hookForm.control}
                render={({ field }) => (selectedOcorrencia && selectedOcorrencia.nomeArquivo1 && selectedOcorrencia.nomeArquivo1.length > 0) ? (
                  <div className="w-full flex flex-row justify-between items-center">
                    <span
                      className="cursor-pointer"
                      onMouseDown={() => handleDownload(1)}>
                      {selectedOcorrencia.nomeArquivo1 && selectedOcorrencia.nomeArquivo1.length > 40
                        ? `${selectedOcorrencia.nomeArquivo1.substring(0, 40)}...`
                        : selectedOcorrencia.nomeArquivo1}
                    </span>
                    <IconButton onClick={() => handleDeleteFile(1)}>
                      <DynamicIcon nameIcon="DeleteForeverRounded" size={25} className="text-red-500" />
                    </IconButton>
                  </div>
                ) : (
                  <PrimaryButton
                    component="label"
                    role={undefined}
                    variant="contained"
                    tabIndex={-1}
                    disabled={saving}
                    startIcon={<DynamicIcon nameIcon="Download" size={25} className="text-white" />}>
                    {field.value ? normalizeFileName(field.value.name, 40) : "Escolher arquivo"}
                    <VisuallyHiddenInput
                      type="file"
                      onChange={(e) => handleFileChange(e, field.onChange, 1)}
                      multiple={false} />
                  </PrimaryButton>
                )} />
            </div>

            <div className="w-[70%] ">
              <DefaultFormInput
                name='descricaoArquivo1'
                label='Descrição do Arquivo'
                reactHookForm={hookForm}
              />
            </div>
          </div>

          <div className="flex flex-row gap-2 w-full justify-between">
            <div className="w-[30%]">
              <Controller
                name='arquivo2'
                control={hookForm.control}
                render={({ field }) => (selectedOcorrencia && selectedOcorrencia.nomeArquivo2 && selectedOcorrencia.nomeArquivo2.length > 0) ? (
                  <div className="w-full flex flex-row justify-between items-center">
                    <span
                      className="cursor-pointer"
                      onMouseDown={() => handleDownload(2)}>
                      {selectedOcorrencia.nomeArquivo2 && selectedOcorrencia.nomeArquivo2.length > 40
                        ? `${selectedOcorrencia.nomeArquivo2.substring(0, 40)}...`
                        : selectedOcorrencia.nomeArquivo2}
                    </span>
                    <IconButton onClick={() => handleDeleteFile(2)}>
                      <DynamicIcon nameIcon="DeleteForeverRounded" size={25} className="text-red-500" />
                    </IconButton>
                  </div>
                ) : (
                  <PrimaryButton
                    component="label"
                    role={undefined}
                    variant="contained"
                    tabIndex={-1}
                    disabled={saving}
                    startIcon={<DynamicIcon nameIcon="Download" size={25} className="text-white" />}>
                    {field.value ? normalizeFileName(field.value.name, 40) : "Escolher arquivo"}
                    <VisuallyHiddenInput
                      type="file"
                      onChange={(e) => handleFileChange(e, field.onChange, 2)}
                      multiple={false} />
                  </PrimaryButton>
                )} />
            </div>
            <div className="w-[70%] ">
              <DefaultFormInput
                name='descricaoArquivo2'
                label='Descrição do Arquivo'
                reactHookForm={hookForm}
              />
            </div>
          </div>

        </>
      </FormContainer>
    </BaseFormControlledModal>
  );
}